import { Address, fromNano } from '@ton/core'
import React, { FC, useCallback, useState } from 'react'
import { BoxV0 } from '@voteboxio/core'
import { useAppContext } from '../contexts/App.context'
import { useAsyncInitialize } from '../hooks/useAsyncInitialize'
import type { BoxDto } from '../types/generated'
import { useVote } from '../hooks/useVote'
import { BoxView } from '../ui/BoxView'
import { Dialog } from '../dialogs/Dialog'
import { EnterVoteAmountDialog } from '../dialogs/EnterVoteAmountDialog'
import { TimeRemaining } from '../ui/TimeRemaining'
import { useBoxStatus } from '../hooks/useBoxStatus'
import { WalletConnectionRequiredDialog } from '../dialogs/WalletConnectionRequiredDialog'
import { TagType } from '../ui/BoxStatusBar'
import { useClaimReward } from '../hooks/useClaimReward'
import { useBoxChoices } from '../hooks/useBoxChoices'

export const Box: FC<{ box: BoxDto; isSingleBox?: boolean }> = ({
  box,
  isSingleBox,
}) => {
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number | null>(
    null,
  )

  const resetSelectedOption = () => {
    setSelectedOptionIndex(null)
  }

  const { client, wallet, authenticated } = useAppContext()

  const boxV0OpenedContract = useAsyncInitialize(async () => {
    if (client) {
      return client.open(BoxV0.BoxV0.fromAddress(Address.parse(box.address)))
    }
  }, [client])

  const { sendVote, isValidVotingAmount } = useVote(
    boxV0OpenedContract,
    box.recommendedVoteGas,
  )

  const { timeLeft, ...boxStatus } = useBoxStatus(box)
  const { days, hours, minutes, isExpired } = timeLeft

  const handleOnBoxChoiceClick = (index: number) => {
    setSelectedOptionIndex(index)
  }

  const { boxChoices } = useBoxChoices({ box })

  const { isClaimable, claimReward } = useClaimReward({
    box: box,
    recommendedVoteGas: box.recommendedVoteGas,
  })

  const getProgressPercentage = (
    createdAt: string,
    deadline: string | number,
  ) => {
    const createdAtMs = new Date(Number(createdAt)).getTime()
    const deadlineMs = Number(deadline)
    const now = Date.now()
    const totalDuration = deadlineMs - createdAtMs
    const elapsed = now - createdAtMs

    return Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100)
  }

  const progressPercentage = getProgressPercentage(box.startTime, box.endTime)
  const voteItIsAlreadyEnding = days < 1 && hours < 1

  const boxTags = (): Record<TagType, boolean> => {
    if (boxStatus.isWinner) {
      // The winning box has two states: collect the reward or the reward has already been taken
      return {
        'collect-reward': boxStatus.canClaimReward,
        'reward-collected': !boxStatus.canClaimReward,
        'no-reward': false,
        ended: false,
      }
    } else if (!boxStatus.isFinish) {
      return {
        'collect-reward': false,
        'reward-collected': false,
        'no-reward': false,
        ended: false,
      }
    }

    // For finished boxes: if user is not authenticated, only show "ended" tag
    // If authenticated and not a winner, show both "no-reward" and "ended" tags
    return {
      'collect-reward': false,
      'reward-collected': false,
      'no-reward': authenticated,
      ended: true,
    }
  }

  const handleConfirm = useCallback(
    async (amount: string) => {
      if (selectedOptionIndex === null) {
        throw new Error('Nullable selectedOptionIndex')
      }

      return sendVote(selectedOptionIndex, amount)
    },
    [selectedOptionIndex, sendVote],
  )

  return (
    <>
      <BoxView
        boxId={box.id}
        title={box.question}
        progressPercentage={progressPercentage}
        expiration={
          !isExpired && (
            <TimeRemaining
              days={days}
              hours={hours}
              minutes={minutes}
              showIcon={voteItIsAlreadyEnding}
            />
          )
        }
        boxChoices={boxChoices}
        onBoxChoiceClick={handleOnBoxChoiceClick}
        isExpired={isExpired}
        imgSrc={box.imageUrl}
        isSingleBox={isSingleBox}
        isUnderModeration={!box.passedModeration}
        tags={boxTags()}
        winnerChoiceIndex={box.winner}
        onRewardClaim={
          isClaimable
            ? async () => {
                try {
                  await claimReward()
                } catch (error) {
                  console.error('Failed to claim reward:', error)
                }
              }
            : undefined
        }
        totalParticipants={box.votedUsersCount}
        totalVotes={Number(
          Number(
            fromNano(
              box.boxChoices.reduce((acc, choice) => {
                return (acc += Number(choice.voteCount))
              }, 0),
            ),
          ).toFixed(1),
        )}
      />

      {selectedOptionIndex !== null && (
        <Dialog onClose={resetSelectedOption}>
          {wallet ? (
            <EnterVoteAmountDialog
              onSubmit={handleConfirm}
              onClose={resetSelectedOption}
              isValidVotingAmount={isValidVotingAmount}
              maxValue={box.maxTonStake}
              minValue={box.minTonStake}
            />
          ) : (
            <WalletConnectionRequiredDialog onClose={resetSelectedOption} />
          )}
        </Dialog>
      )}
    </>
  )
}
